import { useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { scrollToId } from "@/components/SmoothScroll";

const screens = [
  { src: "/app/screen_landing.png", label: "Two journeys, one simple app" },
  {
    src: "/app/screen_garage.png",
    label: "Book a garage nearby and get your motorcycle set up",
  },
  {
    src: "/app/screen_dashboard.png",
    label: "Track upcoming replacements by entering distance driven",
  },
  {
    src: "/app/screen_alerts.png",
    label: "View a snapshot of your upcoming replacements",
  },
  {
    src: "/app/screen_part.png",
    label: "Learn how to maintain each part and the signs to replace it",
  },
  {
    src: "/app/screen_edukaan.png",
    label: "Get the best parts delivered directly to your nearby mechanic",
  },
];

export default function AppGallery() {
  const scrollerRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

  const scrollByCards = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.85) * dir, behavior: "smooth" });
  };

  // Mouse drag-to-scroll (touch devices keep native momentum scrolling).
  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const el = scrollerRef.current;
    drag.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
    el.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.down) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 4) d.moved = true;
    scrollerRef.current.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => {
    drag.current.down = false;
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  // Translate vertical wheel into horizontal scroll while there's room,
  // and release to the page (vertical) at the ends. Non-passive so we can preventDefault.
  const onWheel = useCallback((e) => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (delta === 0) return;
    const max = el.scrollWidth - el.clientWidth;
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft >= max - 1;
    if ((delta > 0 && !atEnd) || (delta < 0 && !atStart)) {
      el.scrollLeft += delta;
      e.preventDefault();
    }
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  return (
    <section
      id="walkthrough"
      data-testid="app-gallery-section"
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 flex items-center justify-center">
        <div className="radial-spot h-[60%] w-[80%] max-w-[800px]" />
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex items-end justify-between gap-6 mb-12"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#E7B459] mb-5">
              The features
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Take a peek at different features.
            </h2>
          </div>

          <div className="hidden md:flex items-center gap-3 shrink-0 pb-1">
            <button
              onClick={() => scrollByCards(-1)}
              data-testid="gallery-prev-btn"
              aria-label="Previous feature"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#F4F1ED] hover:border-[#E7B459] hover:text-[#E7B459] transition-colors duration-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scrollByCards(1)}
              data-testid="gallery-next-btn"
              aria-label="Next feature"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#F4F1ED] hover:border-[#E7B459] hover:text-[#E7B459] transition-colors duration-200"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        <motion.div
          ref={scrollerRef}
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onClickCapture}
          className="flex items-start gap-6 md:gap-8 overflow-x-auto scrollbar-hide touch-pan-x overscroll-x-contain cursor-grab active:cursor-grabbing select-none -mx-6 px-6 md:mx-0 md:px-0 pb-2"
          data-testid="app-gallery-scroller"
          data-lenis-prevent
        >
          {screens.map((s, i) => (
            <motion.figure
              key={s.src}
              variants={fadeUp}
              data-testid={`app-screen-${i + 1}`}
              className="shrink-0 w-[74%] xs:w-[64%] sm:w-[280px] flex flex-col items-center"
            >
              <div className="relative w-full max-w-[280px] aspect-[9/18.3]">
                <div className="absolute inset-x-[8%] inset-y-[1.5%] rounded-[2.4rem] shadow-[0_28px_55px_-18px_rgba(0,0,0,0.75)]" />
                <img
                  src={s.src}
                  alt={s.label}
                  loading="lazy"
                  draggable="false"
                  className="relative z-10 w-full h-full object-contain pointer-events-none"
                />
              </div>
              <figcaption className="mt-6 flex items-start gap-2.5 text-left">
                <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-[#E7B459]" />
                <span className="text-base font-semibold text-[#F4F1ED] leading-snug">
                  {s.label}
                </span>
              </figcaption>
            </motion.figure>
          ))}

          {/* Trailing CTA — top-aligned to match the phones */}
          <motion.div
            variants={fadeUp}
            data-testid="gallery-explore-cta"
            className="shrink-0 w-[74%] xs:w-[64%] sm:w-[280px] flex flex-col items-center"
          >
            <div className="w-full max-w-[280px] aspect-[9/18.3] rounded-[2.4rem] border border-dashed border-[#E7B459]/40 bg-[#E7B459]/5 flex flex-col items-center justify-center text-center px-6 gap-6">
              <p className="font-display text-2xl font-semibold text-[#F4F1ED] leading-tight">
                There's more to Raahi.
              </p>
              <button
                onClick={() => scrollToId("waitlist")}
                data-testid="explore-more-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-[#E7B459] text-[#1A1D24] text-sm font-bold px-6 py-4 hover:bg-[#f0c46e] transition-colors duration-200 shadow-[0_10px_30px_-8px_rgba(231,180,89,0.6)]"
              >
                Explore more features
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        </motion.div>

        <p className="mt-6 text-xs text-[#F4F1ED]/40 flex items-center gap-2 md:hidden">
          <span className="inline-block h-1 w-6 rounded-full bg-[#E7B459]/60" />
          Swipe to explore
        </p>
      </div>
    </section>
  );
}
